import { z } from 'zod';

import { actionPlanSchema } from '../action-plan';
import { actionTaskCreateSchema, actionTaskSchema } from '../action-task';
import { aiStatusSchema } from '../ai';
import { seoStrategyCreateSchema, seoStrategySchema } from '../seo-strategy';
import {
  SeoStrategyKeywordFunnel,
  seoStrategyKeywordCreateSchema,
  seoStrategyKeywordSchema,
} from '../seo-strategy-keywords';
import { mongoIdSchema } from '../shared';

//////////////////////////
// Get
export const getSeoStrategyByBrandIdReqSchema = z.object({
  params: z.object({
    brandId: seoStrategySchema.shape.brandId,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetSeoStrategyByBrandIdReq = z.infer<typeof getSeoStrategyByBrandIdReqSchema>;

const keywordStatsSchema = z.object({
  totalVolume: z.number(),
  distribution: z.object({
    top3: z.number(),
    top10: z.number(),
    top30: z.number(),
    beyond30: z.number(),
    notRanking: z.number(),
  }),
  volumeByFunnel: z.array(
    z.object({
      funnel: z.string(),
      volume: z.number(),
      count: z.number(),
    })
  ),
});

export const getSeoStrategyByBrandIdResSchema = {
  200: z.object({
    data: z.object({
      strategy: seoStrategySchema,
      keywordsTotal: z.number(),
      stats: keywordStatsSchema,
    }),
  }),
};
export type GetSeoStrategyByBrandIdRes = z.infer<(typeof getSeoStrategyByBrandIdResSchema)[200]>;

//////////////////////////
// Save
export const saveSeoStrategyReqSchema = z.object({
  params: z.object({
    brandId: seoStrategySchema.shape.brandId,
  }),
  query: z.object({}).nullish(),
  body: seoStrategyCreateSchema.omit({
    status: true,
    sumOverview: true,
    sumStrengths: true,
    sumOpportunities: true,
    sumWeaknesses: true,
  }),
});
export type SaveSeoStrategyReq = z.infer<typeof saveSeoStrategyReqSchema>;

export const saveSeoStrategyResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type SaveSeoStrategyRes = z.infer<(typeof saveSeoStrategyResSchema)[200]>;

//////////////////////////
// Regenerate
export const regenerateSeoStrategyReqSchema = z.object({
  params: z.object({
    brandId: seoStrategySchema.shape.brandId,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type RegenerateSeoStrategyReq = z.infer<typeof regenerateSeoStrategyReqSchema>;

export const regenerateSeoStrategyResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type RegenerateSeoStrategyRes = z.infer<(typeof regenerateSeoStrategyResSchema)[200]>;

//////////////////////////
// Create Keyword
export const createSeoStrategyKeywordReqSchema = z.object({
  params: z.object({
    brandId: z.string(),
  }),
  query: z.object({}).nullish(),
  body: seoStrategyKeywordCreateSchema.omit({
    brandId: true,
    seoStrategyId: true,
    isGenerated: true,
  }),
});
export type CreateSeoStrategyKeywordReq = z.infer<typeof createSeoStrategyKeywordReqSchema>;

export const createSeoStrategyKeywordResSchema = {
  200: z.object({
    data: z.string(),
  }),
};
export type CreateSeoStrategyKeywordRes = z.infer<(typeof createSeoStrategyKeywordResSchema)[200]>;

//////////////////////////
// Update Keyword
export const updateSeoStrategyKeywordReqSchema = z.object({
  params: z.object({
    brandId: z.string(),
    keywordId: z.string(),
  }),
  query: z.object({}).nullish(),
  body: seoStrategyKeywordCreateSchema
    .omit({
      brandId: true,
      seoStrategyId: true,
      isGenerated: true,
    })
    .partial(),
});
export type UpdateSeoStrategyKeywordReq = z.infer<typeof updateSeoStrategyKeywordReqSchema>;

export const updateSeoStrategyKeywordResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type UpdateSeoStrategyKeywordRes = z.infer<(typeof updateSeoStrategyKeywordResSchema)[200]>;

//////////////////////////
// Remove Keyword
export const removeSeoStrategyKeywordReqSchema = z.object({
  params: z.object({
    brandId: z.string(),
    keywordId: z.string(),
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type RemoveSeoStrategyKeywordReq = z.infer<typeof removeSeoStrategyKeywordReqSchema>;

export const removeSeoStrategyKeywordResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type RemoveSeoStrategyKeywordRes = z.infer<(typeof removeSeoStrategyKeywordResSchema)[200]>;

//////////////////////////
// Search Keywords
export const searchSeoStrategyKeywordsReqSchema = z.object({
  params: z.object({
    brandId: z.string(),
  }),
  query: z
    .object({
      search: z.string().optional(),
      limit: z.coerce.number().optional(),
      offset: z.coerce.number().optional(),
      sortBy: z.string().optional(),
      sortByDir: z.enum(['ASC', 'DESC']).optional(),
      difficultyMin: z.coerce.number().optional(),
      difficultyMax: z.coerce.number().optional(),
      positionMin: z.coerce.number().optional(),
      positionMax: z.coerce.number().optional(),
      funnel: z.string().optional(),
    })
    .nullish(),
  body: z.object({}).nullish(),
});
export type SearchSeoStrategyKeywordsReq = z.infer<typeof searchSeoStrategyKeywordsReqSchema>;

export const searchSeoStrategyKeywordsResSchema = {
  200: z.object({
    data: z.array(seoStrategyKeywordSchema),
    totalCount: z.number(),
  }),
};
export type SearchSeoStrategyKeywordsRes = z.infer<
  (typeof searchSeoStrategyKeywordsResSchema)[200]
>;

//////////////////////////
// Link Keyword to Content
export const linkSeoStrategyKeywordToContentReqSchema = z.object({
  params: z.object({
    brandId: z.string(),
  }),
  query: z.object({}).nullish(),
  body: z.object({
    keyword: z.string().min(1),
    keywordId: mongoIdSchema,
  }),
});
export type LinkSeoStrategyKeywordToContentReq = z.infer<
  typeof linkSeoStrategyKeywordToContentReqSchema
>;

export const linkSeoStrategyKeywordToContentResSchema = {
  200: z.object({
    linked: z.boolean(),
    keywordId: z.string().optional(),
    contentId: z.string().optional(),
  }),
};
export type LinkSeoStrategyKeywordToContentRes = z.infer<
  (typeof linkSeoStrategyKeywordToContentResSchema)[200]
>;

//////////////////////////
// Get Action Plan by Brand Id
export const getActionPlanByBrandIdReqSchema = z.object({
  params: z.object({
    brandId: actionPlanSchema.shape.brandId,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetActionPlanByBrandIdReq = z.infer<typeof getActionPlanByBrandIdReqSchema>;

export const getActionPlanByBrandIdResSchema = {
  200: z.object({
    data: z.object({
      plan: actionPlanSchema,
      list: z.array(actionTaskSchema),
      keywordFunnels: z.record(z.nativeEnum(SeoStrategyKeywordFunnel)),
      keywordUrls: z.record(z.string().nullish()),
    }),
  }),
};
export type GetActionPlanByBrandIdRes = z.infer<(typeof getActionPlanByBrandIdResSchema)[200]>;

//////////////////////////
// Update Action by Id
export const updateActionByIdReqSchema = z.object({
  params: z.object({
    brandId: z.string(),
    actionId: z.string(),
  }),
  query: z.object({}).nullish(),
  body: actionTaskCreateSchema
    .omit({
      brandId: true,
      actionPlanId: true,
    })
    .partial(),
});
export type UpdateActionByIdReq = z.infer<typeof updateActionByIdReqSchema>;

export const updateActionByIdResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type UpdateActionByIdRes = z.infer<(typeof updateActionByIdResSchema)[200]>;

//////////////////////////
// Update socket

export const seoStrategyUpdateSocketResSchema = z.object({
  contentId: z.string(),
  result: z
    .object({
      strategy: seoStrategySchema,
      keywordsTotal: z.number(),
      stats: keywordStatsSchema,
    })
    .nullish(),
  status: aiStatusSchema,
  error: z.string(),
});
export type SeoStrategyUpdateSocketRes = z.infer<typeof seoStrategyUpdateSocketResSchema>;
